import { useEffect, useState, type FormEvent } from 'react';
import type { LlmProvider, LlmSettings } from '@ia/shared';
import { api, errorMessage, type LlmTestResult } from '../api';
import { Alert, ConfirmDialog, Field, Spinner, When } from '../components/ui';

const PRESETS: Record<LlmProvider, { id: string; label: string }[]> = {
  anthropic: [
    { id: 'claude-opus-5', label: 'Claude Opus 5' },
    { id: 'claude-sonnet-5', label: 'Claude Sonnet 5' },
    { id: 'claude-haiku-4-5', label: 'Claude Haiku 4.5' },
  ],
  maximizer: [
    { id: 'gpt-4o-mini', label: 'GPT-4o mini' },
    { id: 'gpt-4o', label: 'GPT-4o' },
    { id: 'gpt-4.1-mini', label: 'GPT-4.1 mini' },
  ],
};
const DEFAULT_MODEL: Record<LlmProvider, string> = { anthropic: 'claude-opus-5', maximizer: 'gpt-4o-mini' };
const PROXY_URL_EXAMPLE = 'https://api-stg.maximizer.com/ai/v1';
const CUSTOM = '__custom__';

type KeyMode = 'keep' | 'replace' | 'remove';

export function LlmSettingsPage() {
  const [settings, setSettings] = useState<LlmSettings | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [enabled, setEnabled] = useState(false);
  const [provider, setProvider] = useState<LlmProvider>('anthropic');
  const [modelChoice, setModelChoice] = useState(DEFAULT_MODEL.anthropic);
  const [customModel, setCustomModel] = useState('');
  const [baseUrl, setBaseUrl] = useState('');
  const [keyMode, setKeyMode] = useState<KeyMode>('keep');
  const [apiKey, setApiKey] = useState('');
  const [confirmRemove, setConfirmRemove] = useState(false);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [testing, setTesting] = useState(false);
  const [test, setTest] = useState<LlmTestResult | null>(null);
  const [testError, setTestError] = useState<string | null>(null);

  const hydrate = (s: LlmSettings) => {
    setSettings(s);
    setEnabled(s.enabled);
    setProvider(s.provider);
    const preset = PRESETS[s.provider].find((p) => p.id === s.model);
    if (preset || !s.model) {
      setModelChoice(preset?.id ?? DEFAULT_MODEL[s.provider]);
      setCustomModel('');
    } else {
      setModelChoice(CUSTOM);
      setCustomModel(s.model);
    }
    setBaseUrl(s.baseUrl ?? '');
    setKeyMode(s.hasApiKey ? 'keep' : 'replace');
    setApiKey('');
  };

  useEffect(() => {
    api.llmSettings().then(hydrate, (e) => setLoadError(errorMessage(e)));
  }, []);

  const changeProvider = (p: LlmProvider) => {
    setProvider(p);
    setModelChoice(DEFAULT_MODEL[p]);
    setCustomModel('');
  };

  const viaProxy = provider === 'maximizer';
  /** The proxy's secret is a Maximizer bearer token; Anthropic's is an API key. Same encrypted slot server-side. */
  const secretLabel = viaProxy ? 'Bearer token' : 'API key';
  const model = modelChoice === CUSTOM ? customModel.trim() : modelChoice;
  const dirty =
    !!settings &&
    (enabled !== settings.enabled ||
      provider !== settings.provider ||
      model !== settings.model ||
      baseUrl.trim() !== (settings.baseUrl ?? '') ||
      (keyMode === 'replace' && apiKey.length > 0) ||
      keyMode === 'remove');

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setSaveError(null);
    setSaved(false);
    if (!model) return setSaveError('Enter a model id.');
    const body: Parameters<typeof api.updateLlmSettings>[0] = { enabled, provider, model };
    if (viaProxy) {
      body.baseUrl = baseUrl.trim() || null;
      if (enabled && !body.baseUrl) return setSaveError('Enter the Maximizer AI proxy URL before enabling the LLM fallback.');
    }
    if (keyMode === 'replace' && apiKey) {
      if (apiKey.trim().length < 10) return setSaveError(`The ${secretLabel.toLowerCase()} looks too short.`);
      body.apiKey = apiKey.trim();
    } else if (keyMode === 'remove') {
      body.apiKey = null;
    }
    if (!viaProxy) {
      if (enabled && keyMode !== 'replace' && !settings?.hasApiKey) {
        return setSaveError('Add an API key before enabling the LLM fallback.');
      }
      if (enabled && keyMode === 'remove') return setSaveError('Disable the LLM fallback before removing the API key.');
      if (enabled && keyMode === 'replace' && !apiKey && !settings?.hasApiKey) {
        return setSaveError('Add an API key before enabling the LLM fallback.');
      }
    }
    setSaving(true);
    try {
      hydrate(await api.updateLlmSettings(body));
      setSaved(true);
      setTest(null);
    } catch (err) {
      setSaveError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const runTest = async () => {
    setTesting(true);
    setTest(null);
    setTestError(null);
    try {
      setTest(await api.testLlm());
    } catch (e) {
      setTestError(errorMessage(e));
    } finally {
      setTesting(false);
    }
  };

  if (loadError)
    return (
      <div className="page">
        <h1>AI / LLM</h1>
        <Alert>{loadError}</Alert>
      </div>
    );
  if (!settings)
    return (
      <div className="page">
        <Spinner />
      </div>
    );

  const testBlocked = dirty
    ? 'Save your changes first'
    : !settings.hasApiKey
      ? settings.provider === 'maximizer'
        ? 'Add a bearer token to test from here; without one the next batch preview is the test'
        : 'Add an API key first'
      : undefined;

  return (
    <div className="page narrow">
      <div className="page-head">
        <h1>Settings · AI / LLM</h1>
      </div>

      <div className="notice-compliance" role="note">
        <strong>Compliance notice.</strong> The LLM is only used as a fallback parser when a portal recipe can't map a
        record. Enabling it sends captured policy data (client names, policy details) to the LLM provider. This requires
        approval from your compliance team (<a href="mailto:itna@maximizer.com">itna@maximizer.com</a>) before use with
        real client data. With the Maximizer AI proxy the data stays inside Maximizer's own AI service, the one the CRM's
        AI features use.
      </div>

      <form className="card form-card" onSubmit={submit}>
        <label className="switch-row">
          <input type="checkbox" role="switch" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} aria-describedby="enabled-hint" />
          <span>
            <strong>Enable LLM fallback parser</strong>
            <span id="enabled-hint" className="hint">
              When off, the LLM is never called; only portal recipes are used to map records.
            </span>
          </span>
        </label>

        <Field label="Provider">
          {(id) => (
            <select id={id} value={provider} onChange={(e) => changeProvider(e.target.value as LlmProvider)}>
              <option value="maximizer">Maximizer AI proxy</option>
              <option value="anthropic">Anthropic</option>
            </select>
          )}
        </Field>

        {viaProxy && (
          <Field label="Endpoint">
            {(id) => (
              <div className="stack">
                <input
                  id={id}
                  type="url"
                  placeholder={PROXY_URL_EXAMPLE}
                  value={baseUrl}
                  onChange={(e) => setBaseUrl(e.target.value)}
                  maxLength={300}
                  spellCheck={false}
                />
                <div className="hint">
                  The Maximizer AI API base, e.g. <code>{PROXY_URL_EXAMPLE}</code>, or the full <code>chat/completions</code> URL. The proxy
                  holds the vendor key.
                </div>
              </div>
            )}
          </Field>
        )}

        <Field label="Model">
          {(id) => (
            <div className="stack">
              <select id={id} value={modelChoice} onChange={(e) => setModelChoice(e.target.value)}>
                {PRESETS[provider].map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.label} ({p.id}){p.id === DEFAULT_MODEL[provider] ? ' — default' : ''}
                  </option>
                ))}
                <option value={CUSTOM}>Custom…</option>
              </select>
              {modelChoice === CUSTOM && (
                <input
                  aria-label="Custom model id"
                  placeholder={viaProxy ? 'e.g. gpt-4.1' : 'e.g. claude-opus-5-20260901'}
                  value={customModel}
                  onChange={(e) => setCustomModel(e.target.value)}
                  maxLength={100}
                  spellCheck={false}
                  autoFocus
                />
              )}
            </div>
          )}
        </Field>

        <div className="field">
          <span className="label">{secretLabel}</span>
          {settings.hasApiKey && keyMode === 'keep' && (
            <div className="key-row">
              <code className="key-hint">{settings.apiKeyHint ?? 'configured'}</code>
              <button type="button" className="btn btn-sm" onClick={() => setKeyMode('replace')}>
                Replace
              </button>
              <button type="button" className="btn btn-sm btn-danger-ghost" onClick={() => setConfirmRemove(true)}>
                Remove
              </button>
            </div>
          )}
          {settings.hasApiKey && keyMode === 'remove' && (
            <div className="key-row">
              <span className="text-failed">The {secretLabel.toLowerCase()} will be removed when you save.</span>
              <button type="button" className="btn btn-sm" onClick={() => setKeyMode('keep')}>
                Undo
              </button>
            </div>
          )}
          {keyMode === 'replace' && (
            <div className="key-row">
              <input
                type="password"
                aria-label={settings.hasApiKey ? `New ${secretLabel.toLowerCase()}` : secretLabel}
                placeholder={settings.hasApiKey ? `Paste the new ${secretLabel.toLowerCase()}` : viaProxy ? 'Paste a Maximizer bearer token' : 'sk-ant-…'}
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                autoComplete="off"
                spellCheck={false}
                className="grow"
              />
              {settings.hasApiKey && (
                <button
                  type="button"
                  className="btn btn-sm"
                  onClick={() => {
                    setApiKey('');
                    setKeyMode('keep');
                  }}
                >
                  Cancel
                </button>
              )}
            </div>
          )}
          <div className="hint">
            {viaProxy
              ? 'Sent as "Authorization: Bearer" on every proxy call. Leave it empty to use the advisor’s own Maximizer token that the extension sends with each batch. '
              : ''}
            Write-only. The {secretLabel.toLowerCase()} is never sent back to the browser; only a short hint is shown.
          </div>
        </div>

        {saveError && <Alert>{saveError}</Alert>}
        {saved && !dirty && <Alert kind="ok">Settings saved.</Alert>}

        <div className="form-actions">
          <button type="submit" className="btn btn-primary" disabled={saving || !dirty}>
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button type="button" className="btn" onClick={() => void runTest()} disabled={testing || !!testBlocked} title={testBlocked}>
            {testing ? 'Testing…' : 'Test connection'}
          </button>
          <span className="spacer" />
          <span className="muted small">
            Last updated {settings.updatedAt ? <When iso={settings.updatedAt} inline /> : 'never'}
            {settings.updatedBy && <> by {settings.updatedBy}</>}
          </span>
        </div>

        {testError && <Alert>{testError}</Alert>}
        {test &&
          (test.ok ? (
            <Alert kind="ok">
              Connection OK — <code>{test.model}</code> answered in {test.latencyMs} ms.
            </Alert>
          ) : (
            <Alert>
              Connection failed for <code>{test.model}</code> after {test.latencyMs} ms: {test.error ?? 'unknown error'}
            </Alert>
          ))}
      </form>

      <ConfirmDialog
        open={confirmRemove}
        title={`Remove ${secretLabel.toLowerCase()}?`}
        confirmLabel="Remove on save"
        danger
        onClose={() => setConfirmRemove(false)}
        onConfirm={() => {
          setKeyMode('remove');
          if (!viaProxy) setEnabled(false);
          setConfirmRemove(false);
        }}
      >
        <p>
          The stored {secretLabel.toLowerCase()} will be deleted when you save
          {viaProxy ? '; the proxy will then use the advisor’s own token from each batch.' : ', and the LLM fallback will be turned off.'}
        </p>
      </ConfirmDialog>
    </div>
  );
}
